import {
  addCalendarDays,
  addCalendarMonths,
  intervalLabel,
  repeatSlots,
  slotsBetween,
  termEndOf,
  type AgreementBilling,
  type RepeatIntervalUnit,
  type RepeatSlot,
} from '@fieldops/shared'
import { money } from '../../lib/format.js'

/**
 * The service agreement builder's draft, and what it will do once created.
 * Decision 96.
 *
 * The builder previews a schedule nobody has saved yet, so it calls the same
 * `repeatSlots` the server does with the rule `agreements.build` will write:
 * due-date anchored, never ending, stopped at the term. Nothing here is
 * stored; the server recomputes every slot from the rule once it exists.
 */

/** One service at one site, as the builder holds it. */
export type DraftService = {
  key: string
  serviceTypeId: string
  name: string
  intervalCount: number
  intervalUnit: RepeatIntervalUnit
  /** Null follows the agreement's start. */
  firstDueOn: string | null
  priceIncTax: number | null
  /** The job the builder was opened from is this service's first visit. */
  fromJob: boolean
}

/** The job the builder was opened from. */
export type BuilderJob = {
  id: string
  number: string
  status: string
  siteId: string
  /** The day it counts as due: its visit, its completion, or today. */
  dueOn: string
  services: ReadonlyArray<{ serviceTypeId: string; name: string; priceIncTax: number | null }>
  /** The imported ServiceM8 series' interval, when it had one. */
  interval: { intervalCount: number; intervalUnit: RepeatIntervalUnit } | null
}

export type Term = {
  startsOn: string
  endsOn: string
  /** Where a new service's first visit starts: the term's start, or today once that has passed. */
  firstVisit?: string
}

export type BuilderSettings = { today: string; leadDays: number; autoCreate: boolean }

/** How often, as the picker offers it. A weekly imported series keeps its own. */
export const FREQUENCIES: ReadonlyArray<{ intervalCount: number; intervalUnit: RepeatIntervalUnit }> = [
  { intervalCount: 1, intervalUnit: 'MONTH' },
  { intervalCount: 2, intervalUnit: 'MONTH' },
  { intervalCount: 3, intervalUnit: 'MONTH' },
  { intervalCount: 4, intervalUnit: 'MONTH' },
  { intervalCount: 6, intervalUnit: 'MONTH' },
  { intervalCount: 12, intervalUnit: 'MONTH' },
]

export const DEFAULT_FREQUENCY = { intervalCount: 3, intervalUnit: 'MONTH' as const }

export const BILLING_LABEL: Record<AgreementBilling, string> = {
  MONTHLY: 'Monthly instalments',
  PER_VISIT: 'Per visit',
  UPFRONT: 'Annual upfront',
}

/** "3 months", "Monthly" — the short form on an Add a service chip. */
export function shortFrequency(interval: { intervalCount: number; intervalUnit: RepeatIntervalUnit }): string {
  return intervalLabel(interval).replace(/^Every /, '')
}

/** A twelve-month term from a start day. */
export function yearFrom(startsOn: string): Term {
  return { startsOn, endsOn: termEndOf(startsOn, null) }
}

/** The first of the month `months` after the one `today` is in. */
export function firstOfMonth(today: string, months: number): string {
  return addCalendarMonths(`${today.slice(0, 7)}-01`, months)
}

export function firstDueOf(service: DraftService, term: Term, job: BuilderJob | null): string {
  if (service.fromJob && job) return job.dueOn
  return service.firstDueOn ?? term.firstVisit ?? term.startsOn
}

/** The service's visits inside the term, as the server will plan them. */
export function draftSlots(
  service: DraftService,
  term: Term,
  settings: Pick<BuilderSettings, 'today' | 'leadDays'>,
  job: BuilderJob | null,
): RepeatSlot[] {
  const firstDueOn = firstDueOf(service, term, job)
  const visits =
    service.fromJob && job
      ? [
          {
            sequence: 1,
            dueOn: firstDueOn,
            moved: false,
            note: null,
            skipped: false,
            skipReason: null,
            job: { id: job.id, status: job.status, completedOn: null, bookedOn: null },
          },
        ]
      : []
  const slots = repeatSlots(
    {
      intervalCount: service.intervalCount,
      intervalUnit: service.intervalUnit,
      anchor: 'DUE_DATE',
      firstDueOn,
      ends: 'NEVER',
      endAfterVisits: null,
      endsOn: null,
      stopped: false,
      termEndsOn: term.endsOn,
    },
    visits,
    { today: settings.today, leadDays: settings.leadDays, until: term.endsOn },
  )
  return slotsBetween(slots, term.startsOn, term.endsOn)
}

export type PlannedJob = {
  key: string
  dueOn: string
  createOn: string
  service: string
  site: string
  /** Made the moment the agreement is, because creation is on and it is due. */
  now: boolean
}

export type BuilderTotals = {
  sites: number
  services: number
  visits: number
  value: number
  jobs: PlannedJob[]
}

/**
 * The summary: how many sites, services and visits, what the term is worth,
 * and the jobs that will be made first. A job is "now" only for a schedule's
 * next visit whose creation day has come — `repeatSlotReady`, which is what
 * `agreements.build` runs after it commits.
 */
export function builderTotals(
  sites: ReadonlyArray<{ id: string; name: string; services: readonly DraftService[] }>,
  term: Term,
  settings: BuilderSettings,
  job: BuilderJob | null,
): BuilderTotals {
  let services = 0
  let visits = 0
  let value = 0
  const jobs: PlannedJob[] = []
  for (const site of sites) {
    for (const service of site.services) {
      const slots = draftSlots(service, term, settings, job)
      services += 1
      visits += slots.length
      value += slots.length * (service.priceIncTax ?? 0)
      for (const slot of slots) {
        if (slot.job) continue
        jobs.push({
          key: `${site.id}-${service.key}-${slot.sequence}`,
          dueOn: slot.dueOn,
          createOn: slot.createOn,
          service: service.name,
          site: site.name,
          now: settings.autoCreate && slot.isNext && slot.createOn <= settings.today,
        })
      }
    }
  }
  // Stable, so a day's jobs stay in the order the sites and services were picked.
  jobs.sort((a, b) => a.dueOn.localeCompare(b.dueOn))
  return { sites: sites.length, services, visits, value, jobs }
}

/** The billing line under the summary's figures. Recorded only; nothing invoices from it. */
export function billingLine(billing: AgreementBilling, value: number): string {
  switch (billing) {
    case 'MONTHLY':
      return `${money(Math.round(value / 12))} a month, 12 instalments`
    case 'UPFRONT':
      return `${money(value)} invoiced at the start`
    default:
      return 'Invoiced after each visit'
  }
}

/** A service first seen at a site: its catalogue price, every three months unless the job says otherwise. */
export function newDraftService(
  serviceType: { id: string; name: string; defaultPriceIncTax: number | null },
  interval: { intervalCount: number; intervalUnit: RepeatIntervalUnit } = DEFAULT_FREQUENCY,
): DraftService {
  return {
    key: `${serviceType.id}-${Math.random().toString(36).slice(2, 8)}`,
    serviceTypeId: serviceType.id,
    name: serviceType.name,
    intervalCount: interval.intervalCount,
    intervalUnit: interval.intervalUnit,
    firstDueOn: null,
    priceIncTax: serviceType.defaultPriceIncTax,
    fromJob: false,
  }
}

/**
 * The job's own services, at the price it was sold for. The first is the
 * schedule the job itself becomes visit 1 of; the others start one interval
 * after it, because the job already covers this visit.
 */
export function servicesFromJob(job: BuilderJob): DraftService[] {
  const interval = job.interval ?? DEFAULT_FREQUENCY
  return job.services.map((service, index) => ({
    key: `job-${service.serviceTypeId}`,
    serviceTypeId: service.serviceTypeId,
    name: service.name,
    intervalCount: interval.intervalCount,
    intervalUnit: interval.intervalUnit,
    firstDueOn:
      index === 0
        ? null
        : interval.intervalUnit === 'WEEK'
          ? addCalendarDays(job.dueOn, interval.intervalCount * 7)
          : addCalendarMonths(job.dueOn, interval.intervalCount),
    priceIncTax: service.priceIncTax,
    fromJob: index === 0,
  }))
}
